import React from "react";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

interface FormRendererProps {
  data: any;
  onChange: (data: any) => void;
  path?: string[];
  getLabel?: (path: string) => string;
}

/**
 * 通用表單渲染器
 * 
 * 自動根據資料結構生成表單：
 * - string → text input / textarea（根據長度）
 * - number → number input
 * - boolean → checkbox
 * - array → 可增刪的列表
 * - object → 巢狀表單
 */
export default function FormRenderer({ data, onChange, path = [], getLabel }: FormRendererProps) {
  if (data === null || data === undefined) {
    return <div className="text-gray-400 text-sm">無資料</div>;
  }

  // 字串
  if (typeof data === "string") {
    const isLong = data.length > 50;
    const isImagePath = data.startsWith("/assets/") || data.startsWith("http");
    
    if (isImagePath) {
      return (
        <div className="space-y-2">
          <input
            type="text"
            value={data}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-green"
            placeholder="圖片 URL"
          />
          {data && (
            <img src={data} alt="預覽" className="w-32 h-32 object-cover rounded border" onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }} />
          )}
        </div>
      );
    }
    
    if (isLong) {
      return (
        <textarea
          value={data}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-green"
        />
      );
    }
    
    return (
      <input
        type="text"
        value={data}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-green"
      />
    );
  }

  // 數字
  if (typeof data === "number") {
    return (
      <input
        type="number"
        value={data}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-green"
      />
    );
  }

  // 布林
  if (typeof data === "boolean") {
    return (
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={data}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 text-brand-green rounded focus:ring-2 focus:ring-brand-green"
        />
        <span className="text-sm text-gray-700">{data ? "是" : "否"}</span>
      </label>
    );
  }

  // 陣列
  if (Array.isArray(data)) {
    return <ArrayEditor data={data} onChange={onChange} path={path} getLabel={getLabel} />;
  }

  // 物件
  if (typeof data === "object") {
    return <ObjectEditor data={data} onChange={onChange} path={path} getLabel={getLabel} />;
  }

  return <div className="text-gray-400 text-sm">不支援的類型</div>;
}

// 陣列編輯器
function ArrayEditor({ data, onChange, path, getLabel }: FormRendererProps) {
  const handleAdd = () => {
    const newItem = data.length > 0 ? { ...data[0] } : {};
    // 清空新項目的值
    if (typeof newItem === "object" && newItem !== null) {
      Object.keys(newItem).forEach(key => {
        if (typeof newItem[key] === "string") newItem[key] = "";
        else if (typeof newItem[key] === "number") newItem[key] = 0;
        else if (typeof newItem[key] === "boolean") newItem[key] = false;
      });
    }
    onChange([...data, newItem]);
  };

  const handleRemove = (index: number) => {
    onChange(data.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, value: any) => {
    const newData = [...data];
    newData[index] = value;
    onChange(newData);
  };

  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">項目 {index + 1}</span>
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="刪除"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <FormRenderer
            data={item}
            onChange={(value) => handleItemChange(index, value)}
            path={[...path, String(index)]}
            getLabel={getLabel}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={handleAdd}
        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-brand-green hover:text-brand-green transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" />
        新增項目
      </button>
    </div>
  );
}

// 物件編輯器
function ObjectEditor({ data, onChange, path, getLabel }: FormRendererProps) {
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});

  const handleFieldChange = (key: string, value: any) => {
    onChange({ ...data, [key]: value });
  };

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-4">
      {Object.entries(data).map(([key, value]) => {
        const fieldPath = [...path, key].join(".");
        const fieldLabel = getLabel ? getLabel(fieldPath) : key;
        const isCollapsible = typeof value === "object" && value !== null && !Array.isArray(value) && Object.keys(value).length > 3;
        const isCollapsed = collapsed[key] || false;

        return (
          <div key={key} className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                {fieldLabel}
              </label>
              {isCollapsible && (
                <button
                  type="button"
                  onClick={() => toggleCollapse(key)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
              )}
            </div>
            {(!isCollapsible || !isCollapsed) && (
              <FormRenderer
                data={value}
                onChange={(newValue) => handleFieldChange(key, newValue)}
                path={[...path, key]}
                getLabel={getLabel}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
